import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { Plus, Pencil, Trash2, Layers, Search, RefreshCw, MoveUp, MoveDown, Check, Zap } from 'lucide-react'
import { ENGINE_LABELS } from '../utils/buyerEngine'

function BuyerSetModal({ buyerSet, onClose, onSaved }) {
  const isEdit = !!buyerSet
  const setId = buyerSet?.id

  const [form, setForm] = useState(() => ({
    name: buyerSet?.name || '',
    engine: buyerSet?.engine || 'waterfall',
    vertical: buyerSet?.vertical || 'loans',
    is_active: buyerSet?.is_active !== false,
    buyer_ids: buyerSet?.buyers?.map(b => b.id || b) || buyerSet?.buyer_ids || []
  }))

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [buyersList, setBuyersList] = useState([])

  useEffect(() => {
    let ignore = false
    api.get('/buyers/').then(res => {
      if (!ignore) setBuyersList(res.data?.items || res.data || [])
    }).catch(() => {})
    return () => { ignore = true }
  }, [])

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const toggleBuyer = (id) => {
    setForm(prev => {
      const exists = prev.buyer_ids.includes(id)
      if (exists) {
        return { ...prev, buyer_ids: prev.buyer_ids.filter(bId => bId !== id) }
      }
      if (prev.engine === 'direct') {
        // A direct_post set holds exactly one buyer - picking a new one
        // swaps it in rather than piling up a second, which the backend
        // rejects outright (409, "may contain exactly one buyer").
        if (prev.buyer_ids.length > 0) {
          toast.error('A Direct Post set can only hold one buyer - replacing the current selection.')
        }
        return { ...prev, buyer_ids: [id] }
      }
      return { ...prev, buyer_ids: [...prev.buyer_ids, id] }
    })
  }

  // Reconciles buyer_ids against the current engine whenever either changes -
  // covers both the user flipping the Routing Engine dropdown AND the buyer
  // list arriving asynchronously after mount (so a set saved before this
  // feature shipped, holding a since-mismatched member, gets cleaned up on
  // open rather than silently failing to save later). Skips while the list
  // is still empty/loading so an engine change made before the fetch
  // resolves doesn't wipe out valid picks it just hasn't seen yet.
  useEffect(() => {
    if (buyersList.length === 0) return
    setForm(prev => {
      const kept = prev.buyer_ids.filter(id => {
        const b = buyersList.find(bb => bb.id === id)
        return b ? (b.engine || 'waterfall') === prev.engine : false
      })
      const trimmed = prev.engine === 'direct' ? kept.slice(0, 1) : kept
      if (trimmed.length === prev.buyer_ids.length) return prev
      const removed = prev.buyer_ids.length - trimmed.length
      toast.error(`${ENGINE_LABELS[prev.engine]} engine: removed ${removed} buyer(s) that no longer match.`)
      return { ...prev, buyer_ids: trimmed }
    })
  }, [form.engine, buyersList])

  const moveBuyerRank = (index, direction) => {
    setForm(prev => {
      const newBuyerIds = [...prev.buyer_ids]
      const targetIdx = index + direction
      if (targetIdx < 0 || targetIdx >= newBuyerIds.length) return prev
      const temp = newBuyerIds[index]
      newBuyerIds[index] = newBuyerIds[targetIdx]
      newBuyerIds[targetIdx] = temp
      return { ...prev, buyer_ids: newBuyerIds }
    })
  }

  const eligibleBuyersList = buyersList.filter(b => (b.engine || 'waterfall') === form.engine)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Buyer Set Name is required.')
      return
    }
    if (!form.vertical.trim()) {
      setError('Vertical identifier is required.')
      return
    }

    setSaving(true)
    setError('')

    const payload = {
      name: form.name.trim(),
      engine: form.engine,
      vertical: form.vertical.trim().toLowerCase(),
      is_active: form.is_active,
      buyer_ids: form.buyer_ids
    }

    try {
      if (isEdit) {
        await api.patch(`/buyer-sets/${setId}`, payload).catch(() => api.put(`/buyer-sets/${setId}`, payload))
        toast.success('Buyer set updated')
      } else {
        await api.post('/buyer-sets/', payload)
        toast.success('Buyer set created')
      }
      onSaved()
      onClose()
    } catch (err) {
      // 409s from this endpoint carry a business-reason detail string
      // (exclusivity/capacity conflicts) written for the person configuring
      // buyer sets to actually understand - surface it verbatim rather than
      // a generic failure toast.
      const detail = err.response?.data?.detail || err.message || 'Error saving buyer set.'
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal partner-modal partner-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 650 }}>
        <div className="partner-modal-header">
          <div>
            <h2>{isEdit ? 'Edit Buyer Set' : 'Create Buyer Set'}</h2>
            <p>Configure lead routing engine, vertical, and assigned member buyers.</p>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="partner-modal-body" style={{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', padding: 24 }}>
          <section className="partner-modal-section">
            <div className="partner-modal-grid partner-modal-grid-2">
              <div className="partner-field">
                <label className="partner-field-label">Set Name *</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={e => updateField('name', e.target.value)}
                  placeholder="e.g. Prime Waterfall Group"
                  required
                />
              </div>

              <div className="partner-field">
                <label className="partner-field-label">Routing Engine *</label>
                <select
                  className="select"
                  value={form.engine}
                  onChange={e => updateField('engine', e.target.value)}
                >
                  <option value="waterfall">Waterfall (Sequential Priority)</option>
                  <option value="ping_post">Ping / Post (Bidding Auction)</option>
                  <option value="direct">Direct (Single Buyer Post)</option>
                </select>
              </div>

              <div className="partner-field">
                <label className="partner-field-label">Vertical Identifier *</label>
                <input
                  className="input"
                  value={form.vertical}
                  onChange={e => updateField('vertical', e.target.value)}
                  placeholder="e.g. loans, debt, auto"
                  required
                />
              </div>

              <div className="partner-field">
                <label className="partner-field-label">Status</label>
                <label className="partner-check-label" style={{ marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => updateField('is_active', e.target.checked)}
                  />
                  Active Set
                </label>
              </div>
            </div>
          </section>

          <section className="partner-modal-section" style={{ marginTop: 20 }}>
            <div className="partner-modal-section-head">
              <h3>Member Buyers ({form.buyer_ids.length} selected)</h3>
              <p>
                {form.engine === 'waterfall'
                  ? 'Order matters: Top buyers receive leads first in waterfall.'
                  : form.engine === 'direct'
                  ? 'A Direct Post set holds exactly one buyer - picking a different buyer replaces the current selection.'
                  : 'Select buyers included in this routing group.'}
                {' '}Only buyers configured for this set's engine are shown.
              </p>
            </div>

            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-raised)', padding: 12 }}>
              {eligibleBuyersList.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {buyersList.length === 0 ? 'No buyers created yet.' : `No buyers configured for the ${ENGINE_LABELS[form.engine]} engine yet.`}
                </div>
              ) : (
                eligibleBuyersList.map(b => {
                  const bId = b.id
                  const selectedIdx = form.buyer_ids.indexOf(bId)
                  const isSelected = selectedIdx !== -1
                  return (
                    <div
                      key={bId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        marginBottom: 6,
                        background: 'var(--bg-surface)',
                        borderRadius: 'var(--radius)',
                        border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleBuyer(bId)}
                        />
                        <div>
                          <strong style={{ fontSize: 13 }}>{b.name}</strong>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                            Min payout: {b.minimum_lead_payout ? `$${b.minimum_lead_payout}` : 'None'}
                          </span>
                        </div>
                      </label>

                      {isSelected && form.engine === 'waterfall' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginRight: 6 }}>Rank #{selectedIdx + 1}</span>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            style={{ padding: 2, height: 24, width: 24 }}
                            disabled={selectedIdx === 0}
                            onClick={() => moveBuyerRank(selectedIdx, -1)}
                          >
                            <MoveUp size={12} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            style={{ padding: 2, height: 24, width: 24 }}
                            disabled={selectedIdx === form.buyer_ids.length - 1}
                            onClick={() => moveBuyerRank(selectedIdx, 1)}
                          >
                            <MoveDown size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </section>

          {error ? <div className="partner-modal-error" style={{ marginTop: 16 }}>{error}</div> : null}

          <div className="partner-modal-actions" style={{ marginTop: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Buyer Set'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function BuyerSetCard({ buyerSet, canManage, onEdit, onDelete }) {
  const isActive = buyerSet.is_active !== false
  const engine = buyerSet.engine || 'waterfall'

  return (
    <div className="card fade-in partners-card">
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{buyerSet.name}</div>
          <div className="partners-card-subtitle">
            Vertical: <strong>{buyerSet.vertical || 'loans'}</strong> | Set ID: {buyerSet.id}
          </div>
        </div>
        <div className="partners-card-badges">
          <span className={`badge ${isActive ? 'badge-green' : 'badge-red'}`}>{isActive ? 'Active' : 'Inactive'}</span>
          <span className="badge badge-purple" style={{ textTransform: 'uppercase' }}>{engine}</span>
        </div>
      </div>

      <div style={{ padding: '12px 16px', background: 'var(--bg-raised)', borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
          Member Buyers ({buyerSet.buyers?.length || buyerSet.buyer_ids?.length || 0})
        </div>
        {buyerSet.buyers && buyerSet.buyers.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {buyerSet.buyers.map((b, idx) => (
              <div key={b.id || idx} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                <span>{idx + 1}. {b.name || `Buyer #${b}`}</span>
                {b.minimum_lead_payout ? <span>${b.minimum_lead_payout} min</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>No buyers attached to this set.</div>
        )}
      </div>

      {canManage && (
        <div className="partners-card-actions" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost partners-card-btn" style={{ flex: 1 }} onClick={() => onEdit(buyerSet)}>
            <Pencil size={13} style={{ marginRight: 4 }} /> Edit Set
          </button>
          <button className="btn btn-ghost partners-card-btn" style={{ color: 'var(--red)' }} onClick={() => onDelete(buyerSet.id)}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  )
}

export default function BuyerSetsPage() {
  const { user, isAdmin } = useAuth()
  const canManage = isAdmin || user?.is_staff

  const [buyerSets, setBuyerSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [editorFor, setEditorFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get('/buyer-sets/', { params: { limit: 50, page_size: 50, per_page: 50 } })
      const items = res.data?.items || res.data?.buyer_sets || res.data || []
      setBuyerSets(items)
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading buyer sets.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleDelete = async (setId) => {
    if (!window.confirm('Are you sure you want to delete this buyer set?')) return
    try {
      await api.delete(`/buyer-sets/${setId}`).catch(() => api.delete(`/buyer-sets/${setId}/`))
      toast.success('Buyer set deleted')
      load()
    } catch (err) {
      console.error('Delete error:', err.response?.data || err)
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message || 'Error deleting buyer set'
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail))
    }
  }

  const filteredSets = buyerSets.filter(s => s.name?.toLowerCase().includes(q.toLowerCase()) || s.vertical?.toLowerCase().includes(q.toLowerCase()))

  return (
    <div className="fade-in page-shell partners-page-shell">
      <section className="page-hero partners-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Routing Engine</span>
          <h1>Buyer Sets & Routing Groups.</h1>
          <p>
            Group buyers into Waterfall, Ping/Post, or Direct routing strategies categorized by vertical.
          </p>
          <div className="hero-chips partners-header-badges">
            <span className="badge badge-purple">Waterfall & Auctions</span>
            {canManage ? <span className="badge badge-green">Full Control</span> : <span className="badge badge-amber">Read-Only</span>}
          </div>
        </div>
        <div className="page-hero-actions partners-toolbar-actions">
          <button className="btn btn-ghost" onClick={load}>Refresh</button>
          {canManage && (
            <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
              + New Buyer Set
            </button>
          )}
        </div>
      </section>

      <section className="card partners-filters partners-filters-panel" style={{ display: 'flex', gap: 12 }}>
        <div className="search-bar search-bar-elevated" style={{ flex: 1 }}>
          <span className="search-icon"><Search size={14} /></span>
          <input
            className="input"
            placeholder="Search by set name or vertical…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {canManage && (
          <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
            <Plus size={14} style={{ marginRight: 6 }} /> New Buyer Set
          </button>
        )}
      </section>

      {loading ? <div className="partner-empty-block">Loading buyer sets…</div> : null}
      {error ? <div className="partner-modal-error partner-page-error">{error}</div> : null}

      {!loading && !error && filteredSets.length === 0 && (
        <div className="card partner-empty-card">
          No buyer sets found.
          <br />
          <span>{canManage ? 'Create a buyer set to start routing leads.' : 'Ask an admin to create routing sets.'}</span>
        </div>
      )}

      {!loading && filteredSets.length > 0 && (
        <div className="partners-grid">
          {filteredSets.map(bs => (
            <BuyerSetCard
              key={bs.id}
              buyerSet={bs}
              canManage={canManage}
              onEdit={item => setEditorFor(item)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {editorFor && (
        <BuyerSetModal
          buyerSet={editorFor === 'create' ? null : editorFor}
          onClose={() => setEditorFor(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}
